import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Observable } from 'rxjs';
import { PipelineEnvironment, StepTask } from '@telespot/domain';
import { PipelineStepCategory, PipelineStepOrderService, PipelineIntegrityService } from '@protocols/domain';
import type { PipelineStepDraftDto, RetrievePipelineDetailOutput, SavePipelineInput } from '@protocols/use-cases';

/**
 * UI-shaped view of a step for the step-management list. `uuid` is the step's
 * positional index in `steps$`. Owned here (the shared lower layer) so both the
 * facade projection and the reorder effect can reference it without the effects
 * layer importing from a sibling component file.
 */
export interface PipelineStepListItem {
  uuid: string;
  order: number;
  task: StepTask;
}

export interface PipelineStepPack {
  order: number;
  items: PipelineStepListItem[];
}

interface PipelineEditorStateModel {
  id: string | null;
  name: { [lang: string]: string };
  description: { [lang: string]: string };
  env: PipelineEnvironment;
  steps: PipelineStepDraftDto[];
  deletedStepIds: string[];
  /*
   * Ids of persisted steps the user has edited in this session. The pipeline
   * detail load seeds steps with placeholder category names, so a pristine
   * persisted step is re-fetched on open to resolve them; once it has been
   * edited locally the staged draft is authoritative and must not be re-fetched
   * (that would resurrect categories the user removed).
   */
  editedStepIds: string[];
  editingIndex: number | null;
  loading: boolean;
  saving: boolean;
  /*
   * Facade-owned i18n key for UI-driven errors (e.g. reorder failures). Effects
   * never write here — they push typed exceptions into the slots below and the
   * facade dispatches them on read.
   */
  error: string | null;
  /*
   * Raw exception from the most recent `loadPipeline` failure. The facade
   * dispatches it to an i18n key in its own namespace.
   */
  loadException: Error | null;
  /*
   * Raw exception from the most recent `save` failure. Same dispatch model as
   * `loadException`, kept separate so the unknown-error fallback can produce
   * the right copy ("load failed" vs "save failed").
   */
  saveException: Error | null;
  /*
   * Signature snapshot captured when a seed completes. `null` until then, so a
   * session that never seeded (or whose load failed) reads as clean. Every
   * seed updater spreads INITIAL_STATE, clearing it per session.
   */
  baselineSignature: string | null;
}

const INITIAL_STATE: PipelineEditorStateModel = {
  id: null,
  name: {},
  description: {},
  env: PipelineEnvironment.CLOUD,
  steps: [],
  deletedStepIds: [],
  editedStepIds: [],
  editingIndex: null,
  loading: false,
  saving: false,
  error: null,
  loadException: null,
  saveException: null,
  baselineSignature: null,
};

@Injectable()
export class PipelineEditorState extends ComponentStore<PipelineEditorStateModel> {
  constructor() {
    super({ ...INITIAL_STATE });
  }

  // ============================================================================
  // Selectors
  // ============================================================================

  public readonly id$ = this.select((s) => s.id);
  public readonly name$ = this.select((s) => s.name);
  public readonly description$ = this.select((s) => s.description);
  public readonly env$ = this.select((s) => s.env);
  public readonly steps$ = this.select((s) => s.steps);
  public readonly deletedStepIds$ = this.select((s) => s.deletedStepIds);
  public readonly editedStepIds$ = this.select((s) => s.editedStepIds);
  public readonly editingIndex$ = this.select((s) => s.editingIndex);
  public readonly loading$ = this.select((s) => s.loading);
  public readonly saving$ = this.select((s) => s.saving);
  public readonly error$ = this.select((s) => s.error);
  public readonly loadException$ = this.select((s) => s.loadException);
  public readonly saveException$ = this.select((s) => s.saveException);
  public readonly baselineSignature$ = this.select((s) => s.baselineSignature);

  public readonly canChangeEnv$ = this.select((s) => s.steps.length === 0);
  public readonly hasSteps$ = this.select((s) => s.steps.length > 0);
  public readonly displayNameValid$ = this.select((s) => PipelineIntegrityService.isDisplayNameValid(s.name));

  public readonly allLabels$: Observable<string[]> = this.select(this.steps$, (steps) => {
    const uuids: string[] = [];
    for (const step of steps) {
      if (step.mode !== 'structured') continue;
      for (const category of step.categories) {
        uuids.push(category.uuid);
        for (const option of category.options) {
          uuids.push(option.uuid);
        }
      }
    }

    return uuids;
  });

  public readonly draft$ = this.select(
    this.id$,
    this.name$,
    this.description$,
    this.env$,
    this.steps$,
    this.deletedStepIds$,
    (id, name, description, env, steps, deletedStepIds): SavePipelineInput => ({
      id: id ?? undefined,
      name,
      description,
      env,
      steps,
      deletedStepIds,
    })
  );

  // ============================================================================
  // Updaters — metadata
  // ============================================================================

  public readonly setName = this.updater((state, name: { [lang: string]: string }) => ({
    ...state,
    name,
  }));

  public readonly setDescription = this.updater((state, description: { [lang: string]: string }) => ({
    ...state,
    description,
  }));

  public readonly setEnv = this.updater((state, env: PipelineEnvironment) => ({ ...state, env }));

  // ============================================================================
  // Updaters — steps
  // ============================================================================

  public readonly appendStep = this.updater((state, step: PipelineStepDraftDto) => ({
    ...state,
    steps: this.renumber([...state.steps, step]),
  }));

  public readonly replaceStep = this.updater((state, payload: { index: number; step: PipelineStepDraftDto }) => {
    const { id } = payload.step;
    const editedStepIds = id && !state.editedStepIds.includes(id) ? [...state.editedStepIds, id] : state.editedStepIds;
    return {
      ...state,
      steps: state.steps.map((s, i) => (i === payload.index ? payload.step : s)),
      editedStepIds,
    };
  });

  public readonly removeStep = this.updater((state, index: number) => {
    const step = state.steps[index];
    if (!step) return state;
    const deletedStepIds = step.id ? [...state.deletedStepIds, step.id] : state.deletedStepIds;
    return {
      ...state,
      steps: this.renumber(state.steps.filter((_, i) => i !== index)),
      deletedStepIds,
    };
  });

  public readonly setSteps = this.updater((state, steps: PipelineStepDraftDto[]) => ({
    ...state,
    steps: this.renumber(steps),
  }));

  /**
   * Rewrites label UUIDs embedded in structured-mode step categories using the
   * `oldUuid → newUuid` mapping emitted by `LabelEffects.consolidate`. Manual
   * steps carry an opaque `params` blob and are left untouched — pending
   * labels only enter pipeline state via the category editor, which always
   * produces structured drafts.
   */
  public readonly remapLabelUuids = this.updater((state, mapping: Map<string, string>) => {
    if (mapping.size === 0) return state;
    return {
      ...state,
      steps: state.steps.map((step) => {
        if (step.mode !== 'structured') return step;
        return {
          ...step,
          categories: step.categories.map(
            (category) =>
              new PipelineStepCategory({
                uuid: mapping.get(category.uuid) ?? category.uuid,
                name: category.name,
                options: category.options.map((option) => ({
                  ...option,
                  uuid: mapping.get(option.uuid) ?? option.uuid,
                })),
              })
          ),
        };
      }),
    };
  });

  public readonly setEditingIndex = this.updater((state, editingIndex: number | null) => ({
    ...state,
    editingIndex,
  }));

  // ============================================================================
  // Updaters — flags
  // ============================================================================

  public readonly setLoading = this.updater((state, loading: boolean) => ({ ...state, loading }));
  public readonly setSaving = this.updater((state, saving: boolean) => ({ ...state, saving }));
  public readonly setError = this.updater((state, error: string | null) => ({ ...state, error }));

  public readonly setBaselineSignature = this.updater((state, baselineSignature: string) => ({
    ...state,
    baselineSignature,
  }));

  // Setting an exception transitions out of the corresponding in-flight phase
  // (loading/saving → false) — that's how "load failed" / "save failed" reach
  // the UI. Clearing (null), called proactively at the start of a fresh attempt
  // to wipe a stale error, must NOT touch the phase flag: the surrounding
  // effect has just flipped it to true and we'd otherwise undo that.
  public readonly setLoadException = this.updater((state, loadException: Error | null) => ({
    ...state,
    loadException,
    loading: loadException === null ? state.loading : false,
  }));

  public readonly setSaveException = this.updater((state, saveException: Error | null) => ({
    ...state,
    saveException,
    saving: saveException === null ? state.saving : false,
  }));

  // ============================================================================
  // Updaters — lifecycle
  // ============================================================================

  public readonly reset = this.updater(() => ({ ...INITIAL_STATE }));

  public readonly seedFromPipeline = this.updater((_state, input: RetrievePipelineDetailOutput) => ({
    ...INITIAL_STATE,
    id: input.id ?? null,
    name: { ...input.name },
    description: { ...input.description },
    env: input.env,
    steps: input.steps,
  }));

  /**
   * Seed the editor from a staged draft (a `SavePipelineInput`) rather than a
   * fresh DB load. Used by deferred-staging consumers so re-opening a pipeline that was edited but not yet
   * persisted shows the staged version. Persisted steps already present in the
   * draft are recorded as locally-edited so the step editor treats the staged
   * draft as authoritative and does not re-fetch (and thus clobber) them.
   */
  public readonly seedFromDraft = this.updater((_state, input: SavePipelineInput) => ({
    ...INITIAL_STATE,
    id: input.id ?? null,
    name: { ...input.name },
    description: { ...input.description },
    env: input.env,
    steps: input.steps,
    deletedStepIds: input.deletedStepIds ?? [],
    editedStepIds: input.steps.flatMap((step) => (step.id ? [step.id] : [])),
  }));

  private renumber(steps: PipelineStepDraftDto[]): PipelineStepDraftDto[] {
    const orders = PipelineStepOrderService.normalize(steps.map((s) => s.order));
    return steps
      .map((s, i) => (s.order === orders[i] ? s : { ...s, order: orders[i] }))
      .sort((a, b) => a.order - b.order);
  }
}
